import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

// Light-only theme
export type ThemeName = 'light';

const STORAGE_KEY = 'ud_theme';

interface ThemeContextValue {
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void; // kept for API compatibility, but effectively no-op
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function getInitialTheme(): ThemeName {
  // Force light as the only/default theme
  return 'light';
}

function applyThemeToDocument(theme: ThemeName) {
  try {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
  } catch {
    // no-op
  }
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme] = useState<ThemeName>(getInitialTheme);

  useEffect(() => {
    // Always apply light theme
    applyThemeToDocument('light');
    try {
      localStorage.setItem(STORAGE_KEY, 'light');
    } catch { /* ignore */ }
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({
    theme: 'light',
    setTheme: () => { /* no-op: only light theme is supported */ },
  }), []);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export function useTheme() : ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
